import {dayNameFormatter, timeFormatter} from '@hconnect/uikit/src/common/utils/dateformatting'
import {Moment} from 'moment-timezone'

type MomentRange = [start: Moment, end: Moment]

/**
 * Returns formatted date with long day name and time considering locale
 *
 * **Examples**
 *
 * longDayNameAndTimeFormatter('2020-10-10T10:45:55Z', 'Europe/Berlin', 'de'): "Samstag 12:45"
 * longDayNameAndTimeFormatter('2020-10-10T10:45:55Z', 'Europe/Berlin', 'de', {hideDay: true}): "12:45"
 *
 * @param datetime  e.g. '2020-10-10T10:45:55Z'
 * @param timezoneId e.g. 'Europe/Berlin'
 * @param locale  e.g. 'de'
 * @param options.hideDay  e.g. false
 * @returns full name of the day and time in locale
 */
export const longDayNameAndTimeFormatter = (
  datetime: Moment,
  timezoneId: string,
  locale: string,
  options?: {hideDay: boolean}
): string => {
  const timezoneOffset = datetime.tz(timezoneId).utcOffset()

  const dayFormatted = dayNameFormatter(datetime, locale, timezoneOffset)
  const timeFormatted = timeFormatter(datetime, locale, timezoneOffset)

  return options?.hideDay ? `${timeFormatted}` : `${dayFormatted} ${timeFormatted}`
}

/**
 * Returns formatted range of dates with long day name and time considering locale.
 * If range is within the same day, the day of the range end is omitted
 *
 * **Examples**
 *
 * longDayNameAndTimeRangeFormatter(
 * ['2020-10-10T10:45:55Z', '2020-10-10T13:00:00Z'], 'Europe/Berlin', 'de'): "Samstag 12:45 - 15:00"
 *
 * @param start  e.g. '2020-10-10T10:45:55Z'
 * @param end  e.g. '2020-10-10T13:00:00Z'
 * @param timezoneId e.g. 'Europe/Berlin'
 * @param locale  e.g. 'de'
 * @returns date range with full name of the day and time in locale
 */
export const longDayNameAndTimeRangeFormatter = (
  [start, end]: MomentRange,
  timezoneId: string,
  locale: string
): string => {
  const isSameDay = start.tz(timezoneId).isSame(end.tz(timezoneId), 'day')

  const startFormatted = longDayNameAndTimeFormatter(start, timezoneId, locale)
  const endFormatted = longDayNameAndTimeFormatter(end, timezoneId, locale, {hideDay: isSameDay})

  return `${startFormatted} - ${endFormatted}`
}
